<script setup lang="ts">
const { t } = useI18n();

export type AttachmentDocKind = 'booking_form' | 'client_brief' | 'listing' | 'contract' | 'market_report' | 'unknown';

export type AttachmentChip = {
  id: string;
  filename: string;
  byteSize: number;
  status: 'uploading' | 'ready' | 'error';
  errorMessage?: string;
  docKind?: AttachmentDocKind;
};

const MAX_ATTACHMENTS = 5;
const ATTACHMENT_MAX_SIZE_MB = 10;

const DOC_KIND_LABEL_KEYS: Record<AttachmentDocKind, string> = {
  booking_form: 'ask.consoleInput.docKindBookingForm',
  client_brief: 'ask.consoleInput.docKindClientBrief',
  listing: 'ask.consoleInput.docKindListing',
  contract: 'ask.consoleInput.docKindContract',
  market_report: 'ask.consoleInput.docKindMarketReport',
  unknown: 'ask.consoleInput.docKindDocument',
};

const {
  modelValue = '',
  disabled = false,
  // Unused by all 3 current call sites (each passes its own placeholder) —
  // left as a static English fallback since a defineProps default can't
  // reactively re-translate on locale change.
  placeholder = 'Ask anything about properties, deals, launches…',
  size = 'lg',
  attachments = [],
  busy = false,
} = defineProps<{
  modelValue?: string;
  disabled?: boolean;
  placeholder?: string;
  size?: 'lg' | 'md';
  attachments?: AttachmentChip[];
  busy?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
  (e: 'submit'): void;
  (e: 'stop'): void;
  (e: 'attach', files: File[]): void;
  (e: 'remove-attachment', id: string): void;
}>();

const big = computed(() => size === 'lg');
const canAttachMore = computed(() => attachments.length < MAX_ATTACHMENTS);
const canSubmit = computed(
  () => !disabled && (modelValue.trim().length > 0 || attachments.some((a) => a.status === 'ready')),
);

const textareaRef = ref<HTMLTextAreaElement | null>(null);
const fileInputRef = ref<HTMLInputElement | null>(null);

const resizeTextarea = async () => {
  await nextTick();
  const t = textareaRef.value;
  if (!t) return;
  t.style.height = 'auto';
  if (!modelValue) {
    t.style.height = '';
    return;
  }
  t.style.height = `${Math.min(t.scrollHeight, big.value ? 180 : 140)}px`;
};

const onInput = (ev: Event) => {
  const t = ev.target as HTMLTextAreaElement;
  emit('update:modelValue', t.value);
  void resizeTextarea();
};

const insertNewlineAtCursor = () => {
  const t = textareaRef.value;
  if (!t || disabled) return;
  const start = t.selectionStart ?? modelValue.length;
  const end = t.selectionEnd ?? modelValue.length;
  const nextValue = `${modelValue.slice(0, start)}\n${modelValue.slice(end)}`;
  emit('update:modelValue', nextValue);
  nextTick(() => {
    const nextPos = start + 1;
    t.selectionStart = nextPos;
    t.selectionEnd = nextPos;
    void resizeTextarea();
  });
};

const onKeydown = (ev: KeyboardEvent) => {
  if (ev.key !== 'Enter') return;

  if (ev.altKey) {
    ev.preventDefault();
    insertNewlineAtCursor();
    return;
  }

  if (!ev.shiftKey && !ev.metaKey && !ev.ctrlKey) {
    ev.preventDefault();
    if (canSubmit.value) emit('submit');
  }
};

const onPickFile = () => {
  if (disabled || !canAttachMore.value) return;
  fileInputRef.value?.click();
};

const onFileChange = (ev: Event) => {
  const input = ev.target as HTMLInputElement;
  const files = input.files ? Array.from(input.files) : [];
  if (files.length) emit('attach', files);
  input.value = '';
};

const formatSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const isRecording = ref(false);
const interimTranscript = ref('');

type SpeechRecognitionResultLike = {
  readonly isFinal: boolean;
  readonly 0: { transcript: string };
};

type SpeechRecognitionEventLike = {
  readonly resultIndex: number;
  readonly results: {
    readonly length: number;
    readonly [index: number]: SpeechRecognitionResultLike;
  };
};

type SpeechRecognitionLike = {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onstart: (() => void) | null;
  onresult: ((ev: SpeechRecognitionEventLike) => void) | null;
  onend: (() => void) | null;
  onerror: (() => void) | null;
  start: () => void;
  stop: () => void;
};

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

let recognition: SpeechRecognitionLike | null = null;

const SpeechRecognitionCtor =
  (typeof window !== 'undefined'
    ? (
        window as Window & {
          SpeechRecognition?: SpeechRecognitionCtor;
          webkitSpeechRecognition?: SpeechRecognitionCtor;
        }
      ).SpeechRecognition ||
      (
        window as Window & {
          SpeechRecognition?: SpeechRecognitionCtor;
          webkitSpeechRecognition?: SpeechRecognitionCtor;
        }
      ).webkitSpeechRecognition
    : null) ?? null;

const voiceSupported = computed(() => !!SpeechRecognitionCtor);

const stopRecording = () => {
  recognition?.stop();
  recognition = null;
  isRecording.value = false;
  interimTranscript.value = '';
};

const toggleRecording = () => {
  if (!SpeechRecognitionCtor || disabled) return;
  if (isRecording.value) {
    stopRecording();
    return;
  }

  recognition = new SpeechRecognitionCtor();
  recognition.continuous = false;
  recognition.interimResults = true;
  recognition.lang = 'en-US';

  recognition.onstart = () => {
    isRecording.value = true;
  };

  recognition.onresult = (ev: SpeechRecognitionEventLike) => {
    let interim = '';
    let final = '';
    for (let i = ev.resultIndex; i < ev.results.length; i++) {
      const t = ev.results[i][0].transcript;
      if (ev.results[i].isFinal) final += t;
      else interim += t;
    }
    interimTranscript.value = interim;
    if (final) {
      const base = modelValue.trimEnd();
      const next = base ? `${base} ${final.trim()}` : final.trim();
      emit('update:modelValue', next);
      void resizeTextarea();
    }
  };

  recognition.onend = () => {
    isRecording.value = false;
    interimTranscript.value = '';
    recognition = null;
  };

  recognition.onerror = () => {
    isRecording.value = false;
    interimTranscript.value = '';
    recognition = null;
  };

  recognition.start();
};

const isDragging = ref(false);
let dragCounter = 0;

const onDragEnter = (ev: DragEvent) => {
  if (disabled || !canAttachMore.value) return;
  ev.preventDefault();
  dragCounter++;
  isDragging.value = true;
};

const onDragOver = (ev: DragEvent) => {
  if (disabled || !canAttachMore.value) return;
  ev.preventDefault();
};

const onDragLeave = () => {
  dragCounter--;
  if (dragCounter <= 0) {
    dragCounter = 0;
    isDragging.value = false;
  }
};

const onDrop = (ev: DragEvent) => {
  ev.preventDefault();
  dragCounter = 0;
  isDragging.value = false;
  if (disabled || !canAttachMore.value) return;
  const files = ev.dataTransfer?.files ? Array.from(ev.dataTransfer.files) : [];
  if (files.length) emit('attach', files);
};

// Paste files from clipboard; leave normal text paste alone.
const onPaste = (ev: ClipboardEvent) => {
  if (disabled || !canAttachMore.value) return;
  const items = ev.clipboardData?.items;
  if (!items) return;
  const files: File[] = [];
  for (const item of items) {
    if (item.kind !== 'file') continue;
    const file = item.getAsFile();
    if (file) files.push(file);
  }
  if (!files.length) return;
  ev.preventDefault();
  emit('attach', files);
};

defineExpose({
  focus: () => {
    textareaRef.value?.focus();
  },
});

watch(
  () => [modelValue, big.value] as const,
  () => {
    void resizeTextarea();
  },
);
</script>

<template>
  <div
    class="relative w-full max-w-full rounded-[16px] border bg-white shadow-[0_12px_32px_-16px_rgba(29,29,27,0.22)] transition-colors"
    :class="[
      isDragging ? 'border-[#D4A032] bg-[#D4A032]/5' : 'border-[#1D1D1B]',
      big ? 'px-4 py-3.5 tablet:px-5 tablet:py-4' : 'px-4 py-3 tablet:px-4 tablet:py-3.5',
    ]"
    @dragenter="onDragEnter"
    @dragover="onDragOver"
    @dragleave="onDragLeave"
    @drop="onDrop"
  >
    <div
      v-if="isDragging"
      class="pointer-events-none absolute inset-0 z-10 flex items-center justify-center gap-2 rounded-[14px] border-2 border-dashed border-[#D4A032]"
    >
      <Icon
        name="lucide:paperclip"
        class="size-4 text-[#D4A032]"
      />
      <span class="text-[13px] font-medium text-[#8A6417]">{{ t('ask.consoleInput.dropToAttach') }}</span>
    </div>
    <div
      v-if="attachments.length"
      class="mb-2 flex flex-col gap-1.5"
    >
      <div
        v-for="att in attachments"
        :key="att.id"
        class="flex items-center gap-2 rounded-lg border border-[#E5E6E1] bg-[#F7F7F4] px-2.5 py-1.5 text-[12.5px]"
      >
        <Icon
          name="lucide:paperclip"
          class="size-4 shrink-0 text-[#70736E]"
        />
        <span
          class="min-w-0 flex-1 truncate font-medium text-[#1D1D1B]"
          :title="att.filename"
          >{{ att.filename }}</span
        >
        <span class="shrink-0 text-[#70736E]">{{ formatSize(att.byteSize) }}</span>
        <span
          v-if="att.status === 'uploading'"
          class="shrink-0 text-[#70736E]"
          >{{ t('ask.consoleInput.extracting') }}</span
        >
        <span
          v-else-if="att.status === 'error'"
          class="shrink-0 text-red-600"
          :title="att.errorMessage"
          >{{ t('ask.consoleInput.failed') }}</span
        >
        <span
          v-else-if="att.docKind && att.docKind !== 'unknown'"
          class="shrink-0 rounded-md bg-[#D4A032]/10 px-1.5 py-0.5 text-[11.5px] font-semibold text-[#8A6417]"
          :title="t('ask.consoleInput.autoDetectedTitle', { kind: t(DOC_KIND_LABEL_KEYS[att.docKind]) })"
          >{{ t(DOC_KIND_LABEL_KEYS[att.docKind]) }}</span
        >
        <span
          v-else
          class="shrink-0 text-green-600"
          >{{ t('ask.consoleInput.ready') }}</span
        >
        <button
          type="button"
          class="grid size-5 shrink-0 place-items-center rounded text-[#70736E] hover:bg-[#E5E6E1] hover:text-[#1D1D1B]"
          :aria-label="t('ask.consoleInput.removeAttachmentAria')"
          @click="emit('remove-attachment', att.id)"
        >
          <Icon
            name="lucide:x"
            class="size-3.5"
          />
        </button>
      </div>
    </div>

    <div class="flex items-center gap-2 tablet:gap-3">
      <span
        class="shrink-0 font-bold leading-[1.6] text-[#D4A032]"
        :class="big ? 'text-[14px] tablet:text-[15px]' : 'text-[12.5px] tablet:text-[13px]'"
        >›</span
      >
      <div class="relative min-w-0 flex-1">
        <textarea
          ref="textareaRef"
          :value="modelValue"
          :placeholder="isRecording ? interimTranscript || t('ask.consoleInput.listeningPlaceholder') : placeholder"
          :disabled="disabled"
          rows="1"
          class="block min-h-[34px] w-full resize-none border-0 bg-transparent px-0 py-[7px] leading-[20px] text-[#1D1D1B] outline-none placeholder:text-[#70736E]/70 disabled:cursor-not-allowed disabled:opacity-50"
          :class="[
            big ? 'text-[14.5px] tablet:text-[15px]' : 'text-[13.5px]',
            isRecording && !modelValue ? 'placeholder:text-[#D4A032]/60 placeholder:italic' : '',
          ]"
          @input="onInput"
          @keydown="onKeydown"
          @paste="onPaste"
        />
      </div>
      <button
        type="button"
        class="grid size-[34px] shrink-0 place-items-center rounded-[9px] border border-[#E5E6E1] bg-white text-[#70736E] hover:bg-[#F7F7F4] disabled:cursor-not-allowed disabled:opacity-50"
        :disabled="disabled || !canAttachMore"
        :aria-label="t('ask.consoleInput.attachFileAria')"
        :title="
          t(
            'ask.consoleInput.attachFileTitle',
            { size: ATTACHMENT_MAX_SIZE_MB, count: MAX_ATTACHMENTS },
            MAX_ATTACHMENTS,
          )
        "
        @click="onPickFile"
      >
        <Icon
          name="lucide:paperclip"
          class="size-4"
        />
      </button>
      <button
        v-if="voiceSupported"
        type="button"
        class="grid size-[34px] shrink-0 place-items-center rounded-[9px] border transition-colors disabled:cursor-not-allowed disabled:opacity-50"
        :class="
          isRecording
            ? 'border-[#D4A032] bg-[#D4A032]/10 text-[#D4A032] animate-pulse'
            : 'border-[#E5E6E1] bg-white text-[#70736E] hover:bg-[#F7F7F4]'
        "
        :disabled="disabled"
        :aria-label="isRecording ? t('ask.consoleInput.stopRecordingAria') : t('ask.consoleInput.voiceInputAria')"
        :title="isRecording ? t('ask.consoleInput.stopRecordingAria') : t('ask.consoleInput.speakYourQuestionTitle')"
        @click="toggleRecording"
      >
        <Icon
          :name="isRecording ? 'lucide:mic-off' : 'lucide:mic'"
          class="size-4"
        />
      </button>
      <button
        type="button"
        class="grid size-[34px] shrink-0 place-items-center rounded-[9px] text-[14px] font-bold transition-colors disabled:cursor-not-allowed disabled:opacity-50"
        :class="
          busy
            ? 'border border-[#E5E6E1] bg-[#1D1D1B] text-white hover:bg-[#2B2B29]'
            : 'bg-[#D4A032] text-[#1D1D1B] hover:bg-[#C4932E]'
        "
        :disabled="busy ? false : !canSubmit"
        :aria-label="busy ? t('ask.consoleInput.stopResponseLabel') : t('ask.consoleInput.sendLabel')"
        :title="busy ? t('ask.consoleInput.stopResponseLabel') : t('ask.consoleInput.sendLabel')"
        @click="busy ? emit('stop') : emit('submit')"
      >
        <Icon
          v-if="busy"
          name="lucide:square"
          class="size-3.5 fill-current"
        />
        <span v-else>↵</span>
      </button>
      <input
        ref="fileInputRef"
        type="file"
        multiple
        accept=".pdf,.docx,.png,.jpg,.jpeg,.tif,.tiff,.bmp,.webp,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/png,image/jpeg,image/tiff,image/bmp,image/webp"
        class="hidden"
        @change="onFileChange"
      />
    </div>
  </div>
</template>
